import React, { useEffect, useRef, useState } from 'react';
import { useAppStore } from '@/store';
import * as api from '@/api/client';
import type { QcFinding } from '@/types';
import { findingId } from '@/lib/qcFindings';
import type { Claim } from '@/types';
import {
  RefreshCw, FileText, Download, AlertTriangle,
  Info, Loader2, CheckCircle, ExternalLink, Edit3, Wrench, X, Save,
  MessageSquare, Lightbulb, ChevronRight, ChevronDown,
} from 'lucide-react';
import StageGateBanner from '@/components/layout/StageGateBanner';
import Stage4GovernanceNote from '@/components/workspace/Stage4GovernanceNote';
import StageAcknowledgment from '@/components/workspace/StageAcknowledgment';

type FindingRowProps = {
  finding: QcFinding;
  reviewed: boolean;
  /** Free-text drafter note for this finding; persists in
   *  stage_3.content.finding_notes keyed by findingId. */
  note: string;
  onToggleReviewed: () => void;
  onJumpToClaim: (claimNumber: number) => void;
  onFix: (claimNumber: number, finding: QcFinding) => void;
  onNoteChange: (next: string) => void;
};

// Severity taxonomy used for card colouring AND bucketing order.
// Keys are lowercased so "Critical" / "BLOCKER" / "major" all resolve.
// Anything unrecognised lands in the info bucket so a stray "note"
// or "advisory" still renders cleanly.
const _CRITICAL_SEVS = new Set(['critical', 'blocker']);
const _WARNING_SEVS  = new Set(['warning', 'warn', 'major']);

function _severityBucket(sev: string): 'critical' | 'warning' | 'info' {
  const s = (sev || '').toLowerCase();
  if (_CRITICAL_SEVS.has(s)) return 'critical';
  if (_WARNING_SEVS.has(s)) return 'warning';
  return 'info';
}


const FindingRow: React.FC<FindingRowProps> = ({
  finding, reviewed, note, onToggleReviewed, onJumpToClaim, onFix, onNoteChange,
}) => {
  // Normalise severity casing so "Critical" / "CRITICAL" / "critical" all
  // render with the right colour + icon instead of silently falling through
  // to the info styling. Extended taxonomy covers the QC prompt families:
  // blocker / critical → red, major / warning → amber, advisory / info /
  // note → blue.
  const sev = (finding.severity || 'info').toString().toLowerCase();
  const bucket = _severityBucket(sev);
  const icon = bucket === 'critical' ? (
    <AlertTriangle size={16} className="text-red-600" />
  ) : bucket === 'warning' ? (
    <AlertTriangle size={16} className="text-amber-600" />
  ) : (
    <Info size={16} className="text-blue-600" />
  );

  const bgColor = bucket === 'critical' ? 'bg-red-50 border-red-200' :
    bucket === 'warning' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200';

  // Expand/collapse state — finding rows auto-collapse when the drafter
  // marks them reviewed (so a 30-finding list compresses as they work
  // through it). Clicking the collapsed header re-expands for audit
  // purposes; un-marking reviewed also auto-expands. Severity-based
  // card colour still shows through in the collapsed header so the
  // list stays scannable.
  const [expanded, setExpanded] = React.useState(!reviewed);
  React.useEffect(() => {
    setExpanded(!reviewed);
  }, [reviewed]);

  // Defensive coercion so a finding with a missing claim_number / category
  // still renders sensibly instead of producing "Claim undefined". Backend
  // normalisation should already supply these but we belt-and-brace in case
  // older rounds of findings were persisted before that ran.
  const claimLabel = finding.claim_number && Number(finding.claim_number) > 0
    ? `Claim ${finding.claim_number}`
    : 'Claim ?';
  const cat = (finding.category || 'General').toString();
  const catKey = cat.replace(/\s/g, '-');
  const hasTarget = typeof finding.claim_number === 'number' && finding.claim_number > 0;

  if (reviewed && !expanded) {
    // Compact single-line card — drafter sees status at a glance and
    // can re-open for audit. Full opacity (not dimmed) so the claim
    // number + severity chips stay legible against the coloured bg.
    return (
      <div
        data-testid={`qc-finding-${finding.claim_number}-${catKey}`}
        className={`border rounded-sm ${bgColor}`}
      >
        <button
          type="button"
          data-testid={`qc-finding-expand-${finding.claim_number}-${catKey}`}
          onClick={() => setExpanded(true)}
          aria-expanded={false}
          className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-white/40 transition-colors"
        >
          <ChevronRight size={14} className="text-zinc-500 shrink-0" />
          {icon}
          <span className="text-[11px] font-mono uppercase text-zinc-500 shrink-0">{sev}</span>
          <span className="text-sm font-medium text-zinc-900 shrink-0">{claimLabel}</span>
          <span className="text-xs text-zinc-600 truncate">{cat} — reviewed</span>
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-green-800 bg-green-100 border border-green-200 rounded-sm px-1.5 py-0.5 shrink-0">
            <CheckCircle size={10} /> Reviewed
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid={`qc-finding-${finding.claim_number}-${catKey}`}
      className={`border rounded-sm p-4 ${bgColor}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono uppercase text-zinc-500">{sev}</span>
            <span className="text-xs text-zinc-400">·</span>
            <span className="text-sm font-medium text-zinc-900">{claimLabel}</span>
            <span className="text-xs text-zinc-400">·</span>
            <span className="text-xs text-zinc-600">{cat}</span>
            {reviewed && (
              <button
                type="button"
                data-testid={`qc-finding-collapse-${finding.claim_number}-${catKey}`}
                onClick={() => setExpanded(false)}
                title="Collapse reviewed finding"
                className="ml-auto inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-900 rounded-sm px-1.5 py-0.5"
              >
                <ChevronDown size={12} /> Collapse
              </button>
            )}
          </div>
          {/* REASON — the model's explanation of why this is a finding.
              Always labelled and always rendered so the drafter can't miss
              it. Empty messages fall back to an italic placeholder so the
              row never collapses to a blank card. */}
          <div className="mb-2" data-testid={`qc-finding-reason-${finding.claim_number}-${catKey}`}>
            <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">
              <MessageSquare size={10} /> Reason
            </div>
            <p className="text-sm text-zinc-800 leading-relaxed">
              {finding.message?.trim() || <span className="italic text-zinc-500">(no message returned by the model — try Re-run QC)</span>}
            </p>
          </div>

          {/* SUGGESTED FIX — the model's recommended next step. Highlighted
              in a slightly brighter sub-card so it reads as actionable,
              not decorative. If empty we explicitly say so because an
              absent fix is itself useful context for the drafter. */}
          <div className="mb-2 rounded-sm border border-white/50 bg-white/60 px-3 py-2" data-testid={`qc-finding-fix-${finding.claim_number}-${catKey}`}>
            <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-green-700 mb-0.5">
              <Lightbulb size={10} /> Suggested fix
            </div>
            <p className="text-sm text-zinc-800 leading-relaxed">
              {finding.suggested_fix?.trim() || <span className="italic text-zinc-500">(no specific fix offered — see Reason above)</span>}
            </p>
          </div>

          {/* DRAFTER NOTE — optional free text the drafter can add per
              finding (e.g. "discussed with inventor — waiting on range"
              or "defer to next draft"). Persists with the findings and
              flows into exports alongside them. Debounced auto-save. */}
          <details className="mb-2 group" data-testid={`qc-finding-note-wrap-${finding.claim_number}-${catKey}`}>
            <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-1 select-none">
              <Edit3 size={10} /> {note ? 'Drafter note' : 'Add drafter note'}
              {note && <span className="text-zinc-400 normal-case font-sans lowercase">— click to edit</span>}
            </summary>
            <textarea
              data-testid={`qc-finding-note-${finding.claim_number}-${catKey}`}
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              rows={2}
              placeholder="e.g. awaiting inventor confirmation on this, defer to next draft…"
              className="mt-1 w-full bg-white border border-zinc-200 rounded-sm px-2 py-1.5 text-xs leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#002FA7] resize-y min-h-[3rem]"
            />
          </details>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid={`qc-fix-claim-${finding.claim_number}-${catKey}`}
              onClick={() => onFix(finding.claim_number, finding)}
              disabled={!hasTarget}
              title={hasTarget ? 'Edit this claim in place' : 'No claim number — cannot fix inline'}
              className="text-xs flex items-center gap-1 px-2 py-1 rounded-sm border border-[#002FA7] bg-[#002FA7] text-white hover:bg-[#002277] transition-colors disabled:bg-zinc-200 disabled:border-zinc-200 disabled:text-zinc-400 disabled:cursor-not-allowed"
            >
              <Wrench size={11} /> Fix inline
            </button>
            <button
              data-testid={`qc-jump-to-claim-${finding.claim_number}`}
              onClick={() => onJumpToClaim(finding.claim_number)}
              disabled={!hasTarget}
              title={hasTarget ? 'Open this claim in Stage 3' : 'No claim number — cannot open'}
              className="text-xs flex items-center gap-1 px-2 py-1 rounded-sm border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ExternalLink size={11} /> Open in Stage 3
            </button>
          </div>
        </div>
        <button
          data-testid={`qc-finding-review-toggle-${finding.claim_number}-${catKey}`}
          onClick={onToggleReviewed}
          className={`text-xs flex items-center gap-1 px-2 py-1 rounded-sm border transition-colors flex-shrink-0 ${
            reviewed ? 'bg-green-100 text-green-800 border-green-200' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'
          }`}
        >
          {reviewed ? <CheckCircle size={12} /> : null}
          {reviewed ? 'Reviewed' : 'Mark reviewed'}
        </button>
      </div>
    </div>
  );
};

export default function Stage4() {
  const { matterState, setMatterState, setActiveStage, setReadOnlyStage, setScrollToClaimId } = useAppStore();
  const matter = matterState?.matter;
  const matterId = matter?.id || '';
  const [unfreezing, setUnfreezing] = useState(false);
  const [confirmUnfreeze, setConfirmUnfreeze] = useState(false);

  // QC findings persist under stage_3.content.qc_findings on the server.
  // Seed the UI from there so the drafter doesn't have to re-run QC on
  // every refresh, and also keep the reviewed state from surviving refresh.
  const s3Content = (matterState?.stages?.stage_3?.content as any) || {};
  const persistedFindings: QcFinding[] = Array.isArray(s3Content.qc_findings) ? s3Content.qc_findings : [];
  const persistedReviewed: string[] = Array.isArray(s3Content.reviewed_findings) ? s3Content.reviewed_findings : [];
  const persistedNotes: Record<string, string> = (s3Content.finding_notes && typeof s3Content.finding_notes === 'object')
    ? s3Content.finding_notes as Record<string, string>
    : {};

  const [findings, setFindings] = useState<QcFinding[]>(persistedFindings);
  const [running, setRunning] = useState(false);
  const [hasRun, setHasRun] = useState<boolean>(persistedFindings.length > 0);
  const [error, setError] = useState<string | null>(null);
  // epi compliance Item 2 — Stage 4 ack. Stored on stage_3's content
  // under the namespaced stage_4_acknowledged_at key because
  // stage_state has no stage_4 row. Used to gate the Export button.
  const persistedAckS4: string | null =
    (s3Content as any)?.stage_4_acknowledged_at || null;
  const [ackAtS4, setAckAtS4] = useState<string | null>(persistedAckS4);
  useEffect(() => { setAckAtS4(persistedAckS4); }, [persistedAckS4]);
  // Parse-status tracking for the transparency fix. 'ok' = rendered
  // normally; 'failed' = show the "could not be parsed" panel with a
  // dedicated retry button. Consecutive failures count toward the
  // 3-retry cap per matter (persisted so a browser refresh doesn't
  // reset the counter). Cap resets on the first successful run.
  const [parseStatus, setParseStatus] = useState<'ok' | 'failed' | null>(
    persistedFindings.length > 0 ? 'ok' : null,
  );
  const [parseReason, setParseReason] = useState<string | null>(null);
  const [parsePreview, setParsePreview] = useState<string | null>(null);
  const FAIL_COUNT_KEY = `stage4.qcFailCount.${matterId}`;
  const [qcFailCount, setQcFailCount] = useState<number>(() => {
    try { return parseInt(localStorage.getItem(FAIL_COUNT_KEY) || '0', 10) || 0; }
    catch { return 0; }
  });
  useEffect(() => {
    try { localStorage.setItem(FAIL_COUNT_KEY, String(qcFailCount)); } catch {}
  }, [FAIL_COUNT_KEY, qcFailCount]);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [downloadingClaimsOnly, setDownloadingClaimsOnly] = useState(false);
  const [reviewedIds, setReviewedIds] = useState<Set<string>>(new Set(persistedReviewed));
  // Per-finding drafter notes, keyed by findingId. Debounced 1s after last
  // keystroke to keep the server write rate sane even when the drafter is
  // typing fast. Persisted under stage_3.content.finding_notes.
  const [findingNotes, setFindingNotes] = useState<Record<string, string>>(persistedNotes);
  const notesDirtyRef = useRef(false);
  // Inline-fix modal state. When set, the Fix dialog is open for this
  // finding + its corresponding claim. Closing the modal without saving
  // leaves everything untouched; saving calls /claim/{id}/fix which
  // bypasses the Stage 3 freeze gate so the drafter stays on Stage 4.
  const [fixTarget, setFixTarget] = useState<null | { claim: Claim; finding: QcFinding }>(null);
  // "Saved, re-run queued" — shows the count of fixes since the last QC
  // so the drafter has a nudge to re-run QC after a few edits.
  const [fixesSinceLastQc, setFixesSinceLastQc] = useState(0);

  // Re-seed when matterState changes (e.g., after navigating back from Stage 3)
  useEffect(() => {
    setFindings(persistedFindings);
    setReviewedIds(new Set(persistedReviewed));
    // Only overwrite local notes with server state when the drafter hasn't
    // typed anything locally since the last load — otherwise a background
    // refresh mid-edit would wipe in-flight changes.
    if (!notesDirtyRef.current) {
      setFindingNotes(persistedNotes);
    }
    setHasRun(persistedFindings.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId, JSON.stringify(persistedFindings), JSON.stringify(persistedReviewed), JSON.stringify(persistedNotes)]);

  // Debounced save for drafter notes. Fires 1s after the last keystroke.
  useEffect(() => {
    if (!notesDirtyRef.current) return;
    const t = setTimeout(async () => {
      try {
        await api.updateFindingNotes(matterId, findingNotes);
        notesDirtyRef.current = false;
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Failed to save drafter notes');
      }
    }, 1000);
    return () => clearTimeout(t);
  }, [findingNotes, matterId]);

  const applyQcResult = (result: api.QcResult) => {
    const status = result.parse_status ?? 'ok';
    if (status === 'failed') {
      setParseStatus('failed');
      setParseReason(result.reason || 'QC result could not be parsed this time.');
      setParsePreview(result.raw_preview || null);
      // Do NOT overwrite existing findings — stale data is better than
      // silently flipping to "All Clear" on a parse failure.
      setQcFailCount((c) => c + 1);
      setHasRun(true);
      return;
    }
    setParseStatus('ok');
    setParseReason(null);
    setParsePreview(null);
    setQcFailCount(0);
    setFindings(result.findings || []);
    setReviewedIds(new Set()); // fresh pass clears reviewed state (server also clears)
    setHasRun(true);
    setFixesSinceLastQc(0);
  };

  const handleRunQc = async () => {
    setRunning(true);
    setError(null);
    try {
      const result = await api.runQc(matterId);
      applyQcResult(result);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'QC review failed. Check LLM settings.');
    }
    setRunning(false);
  };

  const handleRetryQc = async () => {
    setRunning(true);
    setError(null);
    try {
      const result = await api.retryQc(matterId);
      applyQcResult(result);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'QC retry failed. Check LLM settings.');
    }
    setRunning(false);
  };

  const handleOpenFix = (claimNumber: number, finding: QcFinding) => {
    if (!Number.isInteger(claimNumber) || claimNumber <= 0) {
      setError('This finding has no claim number to fix — regenerate QC to refresh.');
      return;
    }
    const target = (matterState?.claims || []).find(c => c.claim_number === claimNumber);
    if (!target) {
      setError(`Claim ${claimNumber} not found on this matter`);
      return;
    }
    setFixTarget({ claim: target, finding });
  };

  const handleFixApplied = async () => {
    // After the modal saves, pull fresh matter state so the claim text in
    // memory reflects the edit (important if the drafter then opens
    // another finding on the same claim).
    try {
      const data = await api.getMatter(matterId);
      setMatterState(data);
    } catch (e: any) {
      // Non-blocking — next re-run QC will pick up the update anyway.
    }
    setFixesSinceLastQc(prev => prev + 1);
    setFixTarget(null);
  };

  const handleToggleReviewed = async (finding: QcFinding) => {
    const id = findingId(finding);
    const next = new Set(reviewedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setReviewedIds(next);
    try {
      await api.updateReviewedFindings(matterId, Array.from(next) as string[]);
    } catch (e: any) {
      // Non-blocking: roll back visual state if the save failed so the UI
      // doesn't lie to the drafter about persistence.
      setReviewedIds(reviewedIds);
      setError(e?.response?.data?.detail || 'Failed to save reviewed state');
    }
  };

  const handleJumpToClaim = (claimNumber: number) => {
    if (!Number.isInteger(claimNumber) || claimNumber <= 0) {
      setError('This finding has no claim number to open.');
      return;
    }
    const target = (matterState?.claims || []).find(c => c.claim_number === claimNumber);
    if (!target) {
      setError(`Claim ${claimNumber} not found on this matter`);
      return;
    }
    // Show Stage 3 read-only and scroll to the claim on mount.
    setReadOnlyStage('stage_3');
    setScrollToClaimId(target.id);
    setActiveStage('stage_3');
  };

  const handleDownloadDocx = async () => {
    setDownloadingDocx(true);
    setError(null);
    try {
      await api.downloadDocxExport(matterId, matter?.title || 'claims');
    } catch (e: any) {
      // Axios with responseType:'blob' returns error bodies as Blobs; try to read them.
      let msg = 'Download failed. Ensure QC has been run and the matter has claims.';
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch { /* keep default */ }
      } else if (e?.response?.data?.detail) {
        msg = e.response.data.detail;
      } else if (e?.message) {
        msg = e.message;
      }
      setError(msg);
    }
    setDownloadingDocx(false);
  };

  const handleDownloadClaimsOnly = async () => {
    setDownloadingClaimsOnly(true);
    setError(null);
    try {
      await api.downloadClaimsOnlyDocx(matterId, matter?.title || 'claims');
    } catch (e: any) {
      let msg = 'Download failed. Ensure Stage 3 is frozen so the claims are committed.';
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch { /* keep default */ }
      } else if (e?.response?.data?.detail) {
        msg = e.response.data.detail;
      } else if (e?.message) {
        msg = e.message;
      }
      setError(msg);
    }
    setDownloadingClaimsOnly(false);
  };

  // Unfreeze Stage 3 so the drafter can fix the claims in response to QC
  // findings. Two-step confirm — unfreezing is a meaningful change: it pops
  // Stage 3 back to editable and drops the "all frozen" guarantee the QC
  // pass was built on. Findings are preserved server-side so they remain
  // visible after the drafter finishes editing and re-freezes.
  const handleUnfreezeStage3 = async () => {
    if (!confirmUnfreeze) {
      setConfirmUnfreeze(true);
      return;
    }
    setUnfreezing(true);
    setError(null);
    try {
      await api.unfreezeStage3(matterId);
      // Refresh full state so top-bar + sidebar reflect the new current stage.
      const data = await api.getMatter(matterId);
      setMatterState(data);
      setReadOnlyStage(null);
      setActiveStage('stage_3');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to unfreeze Stage 3');
    }
    setUnfreezing(false);
    setConfirmUnfreeze(false);
  };

  // Some models return severity with inconsistent casing ("Critical" /
  // "CRITICAL"); normalise via _severityBucket so `blocker` rolls into
  // critical and `major` rolls into warning instead of silently landing
  // in the info bucket (and getting deprioritised in the render order
  // below, which was wrong for a major finding).
  const severityOf = (f: QcFinding) => _severityBucket(f.severity || 'info');
  const criticals = findings.filter(f => severityOf(f) === 'critical');
  const warnings = findings.filter(f => severityOf(f) === 'warning');
  const infos = findings.filter(f => severityOf(f) === 'info');

  return (
    <div className="max-w-4xl mx-auto pt-16 pb-12 px-6">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h1
          data-testid="stage-4-title"
          className="text-2xl font-semibold tracking-tight text-zinc-900"
          style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
        >
          Stage 4 — QC Review & Export
        </h1>
        {hasRun && (
          <span
            className={
              'inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border shrink-0 ' +
              (parseStatus === 'failed'
                ? 'bg-red-50 text-red-800 border-red-200'
                : findings.length === 0
                  ? 'bg-green-50 text-green-800 border-green-200'
                  : 'bg-amber-50 text-amber-800 border-amber-200')
            }
          >
            {parseStatus === 'failed'
              ? 'QC parse failed'
              : findings.length === 0
                ? 'QC clear'
                : `${findings.length} finding${findings.length === 1 ? '' : 's'}`}
          </span>
        )}
      </div>
      <p
        className="text-xs text-zinc-500 mb-4 truncate"
        title="Advisory quality check and final export"
      >
        Advisory quality check and final export
      </p>

      {error && (
        <div data-testid="stage-4-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* Export gate banner. Three states:
          * !hasRun               → blocked: "Run QC before exporting"
          * criticals.length > 0  → warning: structural issues; Fix vs Export anyway
          * everything else       → no banner (exports below are enabled as-is) */}
      {!hasRun && (
        <StageGateBanner
          testId="stage-4-gate-banner"
          status="blocked"
          message="Run QC review before exporting."
          primaryAction={{
            label: running ? 'Running QC…' : 'Run QC',
            onClick: handleRunQc,
            busy: running,
            disabled: running,
          }}
          className="mb-4"
        />
      )}
      {hasRun && criticals.length > 0 && (
        <StageGateBanner
          testId="stage-4-gate-banner"
          status="warning"
          message={`QC found ${criticals.length} structural issue${criticals.length === 1 ? '' : 's'}. Review findings before export.`}
          primaryAction={{
            label: 'Fix issues',
            onClick: handleUnfreezeStage3,
            busy: unfreezing,
          }}
          secondaryAction={{
            label: 'Export anyway',
            onClick: handleDownloadDocx,
            busy: downloadingDocx,
          }}
          className="mb-4"
        />
      )}

      <div className="flex items-center gap-3 mb-8 flex-wrap" data-testid="stage-4-actions">
        <button
          data-testid="run-qc-button"
          onClick={handleRunQc}
          disabled={running}
          className={`rounded-sm px-4 py-2 font-medium transition-colors flex items-center gap-2 disabled:opacity-50 ${
            fixesSinceLastQc > 0
              ? 'bg-green-600 text-white hover:bg-green-700 ring-2 ring-green-200'
              : 'bg-[#002FA7] text-white hover:bg-[#002277]'
          }`}
          title={fixesSinceLastQc > 0
            ? `${fixesSinceLastQc} fix${fixesSinceLastQc === 1 ? '' : 'es'} applied since last QC — re-run to refresh findings`
            : undefined}
        >
          {running ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          {fixesSinceLastQc > 0
            ? `Save & Re-run QC (${fixesSinceLastQc} fix${fixesSinceLastQc === 1 ? '' : 'es'} pending)`
            : hasRun ? 'Re-run QC' : 'Run QC Review'}
        </button>
        <button
          data-testid="export-claims-only-action"
          onClick={handleDownloadClaimsOnly}
          disabled={downloadingClaimsOnly}
          title="Minimal .docx with just the frozen claim text — no analysis, novelty, QC, or provenance sections."
          className="bg-white text-zinc-900 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-4 py-2 font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          {downloadingClaimsOnly ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
          {downloadingClaimsOnly ? 'Preparing .docx…' : 'Claims only (.docx)'}
        </button>
        <button
          data-testid="export-word-action"
          onClick={handleDownloadDocx}
          disabled={downloadingDocx || !ackAtS4}
          title={
            !ackAtS4
              ? 'Check the review-acknowledged box below to enable export'
              : 'Full deliverable: cover page, IDF summary, frozen claims, per-claim analysis, novelty & inventive-step statements, QC findings, and the claim provenance appendix.'
          }
          className="bg-white text-zinc-900 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-4 py-2 font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {downloadingDocx ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          {downloadingDocx ? 'Preparing report…' : 'Download full analysis (.docx)'}
        </button>
        <button
          data-testid="unfreeze-stage-3-button"
          onClick={handleUnfreezeStage3}
          onBlur={() => setConfirmUnfreeze(false)}
          disabled={unfreezing}
          title={confirmUnfreeze ? 'Click again to confirm — this reopens Stage 3 for editing' : 'Unfreeze Stage 3 to fix claims in response to QC findings'}
          className={`rounded-sm px-4 py-2 font-medium transition-colors flex items-center gap-2 disabled:opacity-50 border ${
            confirmUnfreeze
              ? 'bg-amber-600 text-white border-amber-600 hover:bg-amber-700'
              : 'bg-white text-zinc-900 border-zinc-300 hover:bg-zinc-50'
          }`}
        >
          {unfreezing ? <Loader2 size={16} className="animate-spin" /> : <Edit3 size={16} />}
          {confirmUnfreeze && !unfreezing ? 'Confirm — reopen Stage 3' : 'Back to edit claims'}
        </button>
      </div>

      {parseStatus === 'failed' && (
        <div
          data-testid="qc-parse-failed"
          className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-sm"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">
                QC result could not be parsed this time.
              </p>
              <p className="text-xs text-amber-800 mt-1">
                This occasionally happens when the model returns malformed
                output. No findings are being shown until a clean run succeeds
                — the previous "all clear" would not be accurate here.
              </p>
              {parseReason && (
                <p className="text-[11px] text-amber-700 mt-1 font-mono">
                  Reason: {parseReason}
                </p>
              )}
              {qcFailCount >= 3 ? (
                <p
                  data-testid="qc-max-retry-message"
                  className="text-xs text-red-800 mt-3 p-2 bg-red-50 border border-red-200 rounded-sm"
                >
                  QC has failed {qcFailCount} times in a row. Check your
                  OpenRouter API key and model configuration in{' '}
                  <span className="font-medium">Settings → Models</span>.
                </p>
              ) : (
                <button
                  type="button"
                  data-testid="qc-retry-button"
                  onClick={handleRetryQc}
                  disabled={running}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium bg-[#002FA7] text-white hover:bg-[#002277] disabled:opacity-50"
                >
                  {running ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  Run QC Again
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {hasRun && parseStatus !== 'failed' && (
        <div data-testid="qc-findings">
          <div className="flex items-center gap-4 mb-4">
            <h2 className="text-sm font-medium text-zinc-900">Findings ({findings.length})</h2>
            {criticals.length > 0 && (
              <span className="px-2 py-0.5 bg-red-100 text-red-800 border border-red-200 rounded-sm text-xs font-medium">{criticals.length} critical</span>
            )}
            {warnings.length > 0 && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-200 rounded-sm text-xs font-medium">{warnings.length} warning</span>
            )}
            {infos.length > 0 && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 border border-blue-200 rounded-sm text-xs font-medium">{infos.length} info</span>
            )}
          </div>
          {findings.length === 0 ? (
            <div
              data-testid="qc-all-clear"
              className="p-6 bg-green-50 border border-green-200 rounded-sm text-center"
            >
              <CheckCircle size={24} className="text-green-600 mx-auto mb-2" />
              <p className="text-sm text-green-800 font-medium">
                ✓ All checks passed — claims look good!
              </p>
              <p className="text-xs text-green-700/80 mt-2">
                Results look correct? If unexpected,{' '}
                <button
                  type="button"
                  data-testid="qc-all-clear-rerun"
                  onClick={handleRetryQc}
                  disabled={running}
                  className="underline text-green-800 hover:text-green-900 disabled:opacity-50"
                >
                  Run QC Again
                </button>{' '}
                to confirm.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {[...criticals, ...warnings, ...infos].map((f) => {
                const id = findingId(f);
                return (
                  <FindingRow
                    key={id}
                    finding={f}
                    reviewed={reviewedIds.has(id)}
                    note={findingNotes[id] || ''}
                    onToggleReviewed={() => handleToggleReviewed(f)}
                    onJumpToClaim={handleJumpToClaim}
                    onFix={handleOpenFix}
                    onNoteChange={(next) => {
                      notesDirtyRef.current = true;
                      setFindingNotes(prev => {
                        const updated = { ...prev };
                        if (next) updated[id] = next; else delete updated[id];
                        return updated;
                      });
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* epi governance reminder — static block below the QC panel
          and above the acknowledgment checkbox. Stage 4 only;
          complements the always-on ComplianceFooter by appearing at
          the moment of drafting completion when the drafter is most
          likely thinking about export. */}
      <Stage4GovernanceNote />

      {/* epi compliance Item 2 — Stage 4 review acknowledgment.
          Gates the Download / Export Word action above. The ack is
          stored on stage_3's content under a namespaced key (see
          backend _ack_storage_target) because stage_state has no
          stage_4 row. */}
      <div className="mt-3" data-testid="stage-4-acknowledgment-row">
        <StageAcknowledgment
          matterId={matterId}
          stage="stage_4"
          initialAcknowledgedAt={ackAtS4}
          onChange={setAckAtS4}
          onError={setError}
        />
      </div>

      {fixTarget && (
        <FixClaimModal
          claim={fixTarget.claim}
          finding={fixTarget.finding}
          onClose={() => setFixTarget(null)}
          onSaved={handleFixApplied}
        />
      )}
    </div>
  );
}

/**
 * Inline QC-fix modal. Called from Stage 4 finding rows. Gives the drafter
 * the claim text + the finding's message / suggested_fix side-by-side so
 * they can apply the fix without leaving Stage 4. On save, hits
 * POST /claim/{id}/fix which bypasses the Stage 3 freeze gate (see the
 * corresponding backend endpoint for the re-freeze logic).
 */
function FixClaimModal({ claim, finding, onClose, onSaved }: {
  claim: Claim;
  finding: QcFinding;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [text, setText] = useState(claim.claim_text || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const insertPlaceholder = () => {
    // The QC pass now recommends SPECIFY placeholders when critical
    // details are missing. One-click inserts the sentinel at the cursor
    // position (or end of text) so the drafter doesn't have to type the
    // unicode brackets manually.
    const hint = (finding.suggested_fix || 'value from inventor').slice(0, 60);
    const sentinel = `\u27e6SPECIFY: ${hint}\u27e7`;
    setText(prev => prev + (prev && !prev.endsWith(' ') ? ' ' : '') + sentinel);
  };

  const handleSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      await api.fixClaimInline(claim.id, { claim_text: text });
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Failed to save fix');
    }
    setSaving(false);
  };

  const sev = (finding.severity || 'info').toString().toLowerCase();
  const bannerBg = sev === 'critical' ? 'bg-red-50 border-red-200 text-red-900'
    : sev === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-900'
    : 'bg-blue-50 border-blue-200 text-blue-900';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm p-4"
      data-testid="fix-claim-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl bg-white rounded-sm border border-zinc-200 shadow-xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-zinc-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Fix claim {claim.claim_number}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 transition-colors"
            title="Close without saving"
          >
            <X size={16} />
          </button>
        </div>

        <div className={`mb-4 p-3 border rounded-sm text-sm ${bannerBg}`}>
          <div className="font-mono text-[10px] uppercase mb-1">
            {finding.severity} · {finding.category}
          </div>
          <div className="text-sm mb-1">{finding.message}</div>
          {finding.suggested_fix && (
            <div className="text-xs italic">Suggested: {finding.suggested_fix}</div>
          )}
        </div>

        {err && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-800 flex items-center gap-1.5">
            <AlertTriangle size={14} /> {err}
          </div>
        )}

        <label className="text-xs text-zinc-500 block mb-1">Claim text</label>
        <textarea
          data-testid="fix-claim-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent resize-y min-h-[10rem]"
          style={{ fontFamily: 'IBM Plex Serif, serif' }}
        />

        <div className="mt-3 flex items-center justify-between gap-2">
          <button
            type="button"
            data-testid="fix-claim-insert-placeholder"
            onClick={insertPlaceholder}
            className="text-xs text-[#002FA7] hover:text-[#002277] flex items-center gap-1"
            title="Insert a ⟦SPECIFY: …⟧ placeholder when the inventor still needs to supply the detail"
          >
            <Wrench size={12} /> Insert SPECIFY placeholder
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-100 rounded-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="fix-claim-save"
              onClick={handleSave}
              disabled={saving || !text.trim()}
              className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save fix
            </button>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-zinc-400">
          Saves this claim in place without unfreezing Stage 3. After fixing a
          few findings, click <span className="font-medium text-zinc-600">Save &amp; Re-run QC</span> above to refresh the findings list.
        </p>
      </div>
    </div>
  );
}
